import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { BadgeCheck, RefreshCw, ShieldCheck, Star } from 'lucide-react';
import { API, toast } from '@/App';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import Header from '@/components/layout/Header';
import FooterNew from '@/components/layout/FooterNew';
import { logClientError } from '@/utils/clientError';
import { usePublicSeo } from '@/utils/seo';

const CITY_METADATA = {
  BZV: { city: 'Brazzaville', country: 'Congo', label: 'Brazzaville (Congo)' },
  BRAZZAVILLE: { city: 'Brazzaville', country: 'Congo', label: 'Brazzaville (Congo)' },
  PNR: { city: 'Pointe-Noire', country: 'Congo', label: 'Pointe-Noire (Congo)' },
  'POINTE-NOIRE': { city: 'Pointe-Noire', country: 'Congo', label: 'Pointe-Noire (Congo)' },
  KIN: { city: 'Kinshasa', country: 'RDC', label: 'Kinshasa (RDC)' },
  KINSHASA: { city: 'Kinshasa', country: 'RDC', label: 'Kinshasa (RDC)' },
};

const PUBLIC_BADGE_LABELS = {
  pro_seller: 'Pro',
  top_seller: 'Top vendeur',
  fast_shipper: 'Expedition rapide',
  trusted_vendor: 'Fiable',
};

const getVendorLocation = (rawCity) => {
  const normalized = String(rawCity || '').trim().toUpperCase();
  if (CITY_METADATA[normalized]) {
    return CITY_METADATA[normalized];
  }

  if (!normalized) {
    return { city: '-', country: '-', label: '-' };
  }

  return {
    city: String(rawCity || '').trim(),
    country: 'Autre',
    label: String(rawCity || '').trim(),
  };
};

const PublicVendorsPage = () => {
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [verifiedOnly, setVerifiedOnly] = useState(true);
  const [proOnly, setProOnly] = useState(false);
  const [minRating, setMinRating] = useState('0');
  const [selectedCity, setSelectedCity] = useState('all');
  const [selectedCountry, setSelectedCountry] = useState('all');

  usePublicSeo({
    title: 'Vendeurs vérifiés | Coli Noki',
    description:
      'Consultez la liste publique des vendeurs vérifiés Coli Noki, leurs badges de confiance et leur note client.',
    path: '/vendors',
  });

  const loadVendors = async ({ silent = false } = {}) => {
    if (!silent) {
      setLoading(true);
    } else {
      setRefreshing(true);
    }
    try {
      const ratingValue = Number(minRating);
      const response = await axios.get(`${API}/public/vendors`, {
        params: {
          verified_only: verifiedOnly,
          pro_only: proOnly,
          min_rating: Number.isFinite(ratingValue) ? ratingValue : undefined,
          page: 1,
          page_size: 100,
        },
      });
      setVendors(response.data.items || []);
    } catch (error) {
      logClientError('public_vendors.load', error);
      toast.error(error.response?.data?.detail || 'Impossible de charger les vendeurs publics');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    loadVendors();
  }, [verifiedOnly, proOnly, minRating]);

  const normalizedVendors = useMemo(
    () =>
      vendors.map((vendor) => {
        const location = getVendorLocation(vendor.city);
        return {
          ...vendor,
          locationLabel: location.label,
          locationCity: location.city,
          locationCountry: location.country,
          publicBadges: (vendor.badges || [])
            .filter((badge) => badge?.badge_type && !['verified_seller', 'pro_seller'].includes(badge.badge_type))
            .map((badge) => ({
              ...badge,
              label: PUBLIC_BADGE_LABELS[badge.badge_type] || badge.badge_type,
            })),
        };
      }),
    [vendors]
  );

  const cityOptions = useMemo(
    () =>
      Array.from(new Set(normalizedVendors.map((vendor) => vendor.locationCity).filter(Boolean).filter((value) => value !== '-'))).sort(),
    [normalizedVendors]
  );

  const countryOptions = useMemo(
    () =>
      Array.from(
        new Set(normalizedVendors.map((vendor) => vendor.locationCountry).filter(Boolean).filter((value) => value !== '-'))
      ).sort(),
    [normalizedVendors]
  );

  const filteredVendors = useMemo(
    () =>
      normalizedVendors.filter((vendor) => {
        if (selectedCity !== 'all' && vendor.locationCity !== selectedCity) {
          return false;
        }
        if (selectedCountry !== 'all' && vendor.locationCountry !== selectedCountry) {
          return false;
        }
        return true;
      }),
    [normalizedVendors, selectedCity, selectedCountry]
  );

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <Header />
      <main className="flex-1">
        <div className="container mx-auto px-4 py-8 space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="text-2xl flex items-center gap-2">
                <BadgeCheck className="w-6 h-6 text-cyan-600" />
                Vendeurs vérifiés
              </CardTitle>
              <CardDescription>
                Liste publique des vendeurs avec badges de confiance et note client.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-6 gap-4">
                <label className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={verifiedOnly}
                    onChange={(event) => setVerifiedOnly(event.target.checked)}
                  />
                  Vendeurs vérifiés uniquement
                </label>
                <label className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={proOnly}
                    onChange={(event) => setProOnly(event.target.checked)}
                  />
                  Vendeurs Pro uniquement
                </label>
                <div>
                  <Label>Note minimum</Label>
                  <Input
                    type="number"
                    min="0"
                    max="5"
                    step="0.1"
                    value={minRating}
                    onChange={(event) => setMinRating(event.target.value)}
                  />
                </div>
                <div>
                  <Label>Ville</Label>
                  <Select value={selectedCity} onValueChange={setSelectedCity}>
                    <SelectTrigger>
                      <SelectValue placeholder="Toutes les villes" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Toutes les villes</SelectItem>
                      {cityOptions.map((city) => (
                        <SelectItem key={city} value={city}>
                          {city}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Pays</Label>
                  <Select value={selectedCountry} onValueChange={setSelectedCountry}>
                    <SelectTrigger>
                      <SelectValue placeholder="Tous les pays" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Tous les pays</SelectItem>
                      {countryOptions.map((country) => (
                        <SelectItem key={country} value={country}>
                          {country}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex items-end">
                  <Button variant="outline" onClick={() => loadVendors({ silent: true })} disabled={refreshing}>
                    <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
                    Actualiser
                  </Button>
                </div>
              </div>
              <p className="text-sm text-gray-600">
                Besoin de comparer les options ? <Link to="/pricing" className="text-cyan-700 hover:underline">Voir les tarifs</Link> ou{' '}
                <Link to="/register" className="text-cyan-700 hover:underline">créer un compte vendeur</Link>.
              </p>
            </CardContent>
          </Card>

          {loading ? (
            <div className="py-16 text-center text-gray-500">Chargement des vendeurs...</div>
          ) : filteredVendors.length === 0 ? (
            <div className="py-16 text-center text-gray-500">Aucun vendeur ne correspond à ces filtres.</div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {filteredVendors.map((vendor) => (
                <Card key={vendor.vendor_id}>
                  <CardHeader>
                    <CardTitle className="text-lg">{vendor.vendor_name || 'Vendeur'}</CardTitle>
                    <CardDescription>{vendor.locationLabel}</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <div className="flex flex-wrap gap-2">
                      {vendor.verified_status && (
                        <Badge className="bg-green-100 text-green-700">
                          <ShieldCheck className="mr-1 h-3.5 w-3.5" />
                          Vérifié
                        </Badge>
                      )}
                      {vendor.pro_status && <Badge className="bg-indigo-100 text-indigo-700">Pro</Badge>}
                      {(vendor.publicBadges || []).map((badge) => (
                        <Badge key={`${vendor.vendor_id}-${badge.badge_type}`} variant="outline">
                          {badge.label}
                        </Badge>
                      ))}
                    </div>
                    <div className="text-sm text-gray-700 flex items-center gap-2">
                      <Star className="w-4 h-4 text-amber-500" />
                      {Number(vendor.rating || 0).toFixed(2)} / 5
                      <span className="text-gray-500">({vendor.total_reviews || 0} avis)</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button asChild size="sm" variant="outline">
                        <Link to={vendor.review_link_path || `/vendors/${vendor.vendor_id}/review`}>
                          Laisser un avis
                        </Link>
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      </main>
      <FooterNew />
    </div>
  );
};

export default PublicVendorsPage;

